import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import tinyColor from 'tinycolor2';
import { getAcronym, strToColor, parseCSSUnit, hasValue } from '@utils';
import Icon from '../../icons/Icon';
import { hasIcon } from '@icon/utils';
/**
 * Display a profile picture. Supports images (URLs), icons (FontAwesome, Phosphor patterns), and initials.
 *
 * @export
 */
const ProfileImage = (props) => {
    const { image, name, bgColor, textColor: customTextColor, className, style, size = 'medium', shape = 'circle', skipAcronym = false, borderColor, borderWidth = '0' } = props;
    // Convert size to CSS unit string (supports px, rem, em, etc.)
    const getSize = (sizeValue) => {
        if (!sizeValue || sizeValue === 'medium')
            return '3rem';
        if (sizeValue === 'small')
            return '2rem';
        if (sizeValue === 'large')
            return '4rem';
        // For custom sizes, validate using parseCSSUnit
        const parsed = parseCSSUnit(sizeValue);
        return parsed ? sizeValue : '3.33rem'; // fallback if invalid
    };
    const calculatedSize = getSize(size);
    const hasImage = hasIcon(image);
    const hasName = hasValue(name);
    // Parse size to calculate font sizes (maintaining the same unit)
    const parsedSize = parseCSSUnit(calculatedSize);
    const initialsFontSize = parsedSize
        ? `${parsedSize.value * 0.4}${parsedSize.unit}`
        : '1.33rem'; // fallback
    const iconFontSize = parsedSize
        ? `${parsedSize.value * 0.6}${parsedSize.unit}`
        : '2rem'; // fallback
    // Determine content type and render appropriate content
    let content = null;
    let contentType = null;
    if (hasImage) {
        contentType = 'image';
        content = (_jsx("div", { className: "uxpcore_profileimage__image", style: { fontSize: iconFontSize }, children: _jsx(Icon, { icon: image }) }));
    }
    else if (hasName) {
        contentType = 'initials';
        content = (_jsx("div", { className: "uxpcore_profileimage__initials", style: { fontSize: initialsFontSize }, children: skipAcronym ? name : getAcronym(name, 2) }));
    }
    // Background color logic
    let _bgColor;
    if (bgColor && bgColor.trim().length > 0) {
        _bgColor = tinyColor(bgColor);
    }
    else {
        if (contentType === 'initials' && hasName) {
            // Generate consistent color from name
            _bgColor = tinyColor(strToColor(name));
        }
        else {
            // Neutral color for images or empty state
            _bgColor = tinyColor("#e3e3e3");
        }
    }
    // Auto-determine text color based on background darkness (only for initials)
    let textColor = customTextColor || '#424242';
    if (contentType === 'initials' && _bgColor.isDark()) {
        textColor = "#ffffff";
    }
    const finalBgColor = _bgColor.toHex8String();
    // Determine if we should apply size class or inline style
    const isNamedSize = size === 'small' || size === 'medium' || size === 'large';
    const sizeClass = isNamedSize ? size : null;
    const inlineSize = !isNamedSize ? { width: calculatedSize, height: calculatedSize } : {};
    // Container styles
    const containerStyle = Object.assign(Object.assign(Object.assign(Object.assign({ backgroundColor: finalBgColor }, (contentType === 'initials' && { color: textColor })), (borderColor && { border: `${borderWidth} solid ${borderColor}` })), inlineSize), style);
    return (_jsx("div", { className: classNames("uxpcore_profileimage__container", sizeClass, shape, className), style: containerStyle, children: content }));
};
export default ProfileImage;
